import { App as AntApp, ModalFuncProps } from "antd";
import { ActionType, OrderType } from "../types/enums";
import { Modal, Text } from "../components";
import { AppContext } from "../contexts";
import { useContext } from "react";
import { useAppStore } from "../stores";
import { Tools } from "../utils";

const useApp = () => {
  const {
    cart,
    banners = [],
    location,
    cities = [],
    areas = [],
    cartItems = [],
    openingHours = [],
    deliveryTime,
    minimumOrder,
    bootstrap,
    logoUrl,
    payments = [],
    isSiteActive = true,
    isReady = false,
    dispatch,
  } = useContext(AppContext);
  const { modal, message } = AntApp.useApp();

  const today = Tools.date().format("dddd").toLowerCase();
  const todayOpeningHours = openingHours.find((row) => {
    const days = (row.days ?? []).map((day) => String(day).toLowerCase());
    return !days.length || days.includes(today);
  });
  const isPickup = location?.orderType === OrderType.pickup;

  const saveLocation = (payload: any) => {
    const city = cities.find((item) => item.uuid === payload.city);
    const next = {
      ...payload,
      city,
      area: city?.areas?.find((item) => item.uuid === payload.area),
      branch: city?.branches?.find((item) => item.uuid === payload.branch),
    };
    useAppStore.getState().setLocation({
      city: next.city.uuid,
      area: next.area?.uuid,
      branch: next.branch?.uuid,
      orderType: next.orderType,
    });
    dispatch({ type: ActionType.setLocation, payload: next });
  };

  const getTimeBannerMessage = () => {
    if (!isSiteActive)
      return "We are temporarily closed and not accepting orders.";
    if (
      !!todayOpeningHours &&
      !Tools.isBetweenTime(todayOpeningHours.open, todayOpeningHours.close)
    )
      return [
        "Sorry we are closed right now will re-open at ",
        <Text
          weight={"700"}
          level={"text"}
          color="inherit"
          children={Tools.formatTime(todayOpeningHours.open)}
        />,
      ];
    if (
      !!location?.area?.openingHours &&
      !Tools.isBetweenTime(
        location.area.openingHours.open,
        location.area.openingHours.close,
      )
    )
      return [
        "Sorry we don't deliver to this area after ",
        <Text
          weight={"700"}
          level={"text"}
          color="inherit"
          children={Tools.date(
            location.area.openingHours.close,
            "HH:mm",
          ).format("hh:mm A")}
        />,
      ];
  };

  const showModal = ({
    content,
    title,
    onClose = () => {},
    closable = true,
    style,
    styles,
    classNames,
    manageHistory = true,
  }: ModalFuncProps & {
    manageHistory?: boolean;
    onClose?: (e?: unknown) => void;
  }) => {
    let tracked = !!(closable && manageHistory);
    let destroy = () => {};

    const release = (fromBack = false) => {
      if (!tracked) return;
      tracked = false;
      window.removeEventListener("popstate", onPopState);
      if (!fromBack) window.history.back();
    };

    const onPopState = () => {
      release(true);
      destroy();
      onClose({} as any);
    };

    const close = (e?: any) => {
      if (!closable) return;
      release();
      destroy();
      onClose(e);
    };

    if (tracked) {
      window.history.pushState(null, "");
      window.addEventListener("popstate", onPopState);
    }

    const modalRef = modal.confirm({
      closable,
      maskClosable: !!closable,
      onCancel: (e) => close(e),
      className: "app-modal",
      transitionName: "app-modal-transition",
      classNames: { wrapper: "app-modal-wrapper" },
      modalRender: () => (
        <Modal.Body
          style={style}
          title={title}
          close={close}
          styles={styles}
          children={content}
          closable={closable}
          classNames={classNames}
          className="app-modal-body"
        />
      ),
    });

    destroy = modalRef.destroy.bind(modalRef);
    modalRef.destroy = () => {
      release();
      destroy();
    };

    return modalRef;
  };

  const confirm = ({
    title,
    description,
    confirmText = "Confirm",
    cancelText = "Cancel",
    onConfirm,
  }: {
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
  }) => {
    let dialog: { destroy: () => void } | undefined;
    const close = () => dialog?.destroy();

    dialog = showModal({
      title,
      onClose: close,
      content: (
        <Modal.Dialog
          description={description}
          buttons={[
            {
              title: confirmText,
              color: "base",
              onClick: () => {
                close();
                onConfirm();
              },
            },
            {
              title: cancelText,
              outlined: true,
              color: "secondary",
              onClick: close,
            },
          ]}
        />
      ),
    });
  };

  return {
    cart,
    areas,
    cities,
    message,
    banners,
    isPickup,
    cartItems,
    location,
    bootstrap,
    logoUrl,
    payments,
    isSiteActive,
    isReady,
    openingHours,
    deliveryTime,
    minimumOrder,
    todayOpeningHours,
    orderOptions: bootstrap?.orderOptions,
    saveLocation,
    modal: { ...modal, show: showModal, confirm },
    timeBannerMessage: getTimeBannerMessage(),
  };
};

export default useApp;
